import jsPDF from 'jspdf';
import { renderFacilityInformation } from '../../../../utils/pdfHelpers';

/**
 * Generates a PDF invoice for a Guest Checkout record (Inventory module).
 * The layout closely follows the PrescriptionPad print-preview and the
 * Product Invoice PDF styling so that the look-and-feel remains consistent
 * across the application.
 *
 * @param {Object} invoiceData   – The guest-checkout invoice object returned
 *                                 from the backend (includes product_details).
 * @param {Object|null} officeData – Office / facility settings (optional).
 */
export const generateGuestInvoicePDF = async (
  invoiceData,
  officeData = null
) => {
  if (!invoiceData) return;

  const pdf = new jsPDF('p', 'mm', 'letter');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  const margin = 20;
  const lineHeight = 7;
  let yPos = margin;

  /* ---------------------------- Header ---------------------------- */
  pdf.setFontSize(24);
  pdf.setTextColor(41, 128, 185);
  pdf.text('GUEST INVOICE', pageWidth / 2, yPos, { align: 'center' });
  yPos += lineHeight * 2.5;

  /* ---------------- Two-column: Facility | Customer --------------- */
  const leftX = margin;
  const rightX = pageWidth / 2 + 10;
  const colWidth = (pageWidth - margin * 2 - 20) / 2;
  const sectionStartY = yPos;

  // Facility (left)
  const facilityEndY = renderFacilityInformation(pdf, officeData, {
    startX: leftX,
    startY: yPos,
    lineHeight,
    columnWidth: colWidth,
  });
  yPos = Math.max(yPos, facilityEndY);

  // Customer (right)
  let custY = sectionStartY;
  pdf.setFontSize(12);
  pdf.setTextColor(52, 73, 94);
  pdf.text('Customer Information', rightX, custY);
  custY += lineHeight;

  pdf.setFontSize(9);
  pdf.setTextColor(0, 0, 0);

  if (invoiceData.patient_name) {
    pdf.text(`Name: ${invoiceData.patient_name}`, rightX, custY);
    custY += lineHeight * 0.8;
  }

  if (invoiceData.issue_date) {
    const issueDate = new Date(invoiceData.issue_date);
    pdf.text(
      `Date: ${issueDate.toLocaleDateString()} ${issueDate.toLocaleTimeString()}`,
      rightX,
      custY
    );
    custY += lineHeight * 0.8;
  }

  // Invoice details under customer section
  custY += lineHeight * 0.5;
  pdf.setDrawColor(220, 220, 220);
  pdf.line(rightX, custY, rightX + colWidth, custY);
  custY += lineHeight;

  pdf.setFontSize(10);
  pdf.setTextColor(52, 73, 94);
  pdf.text('Invoice Information', rightX, custY);
  custY += lineHeight;

  pdf.setFontSize(9);
  pdf.setTextColor(0, 0, 0);

  if (invoiceData.invoice_id) {
    pdf.text(`Invoice ID: ${invoiceData.invoice_id}`, rightX, custY);
    custY += lineHeight * 0.8;
  }
  if (invoiceData.status) {
    const statusTxt = invoiceData.status.charAt(0).toUpperCase() + invoiceData.status.slice(1);
    pdf.text(`Status: ${statusTxt}`, rightX, custY);
    custY += lineHeight * 0.8;
  }
  if (invoiceData.added_by) {
    pdf.text(`Issued By: ${invoiceData.added_by}`, rightX, custY);
    custY += lineHeight * 0.8;
  }

  yPos = Math.max(yPos, custY);

  /* ---------------------- Divider before table -------------------- */
  yPos += lineHeight * 1.5;
  pdf.setDrawColor(52, 73, 94);
  pdf.line(margin, yPos, pageWidth - margin, yPos);
  yPos += lineHeight * 1.5;

  /* ---------------------- Sale-items table ------------------------ */
  // Currency settings - use office settings if available, fallback to invoice data or default
  const currency = officeData?.local_currency || invoiceData.currency || 'USD';
  const currencySymbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency === 'PKR' ? 'PKR ' : currency === 'GBP' ? '£' : currency === 'JPY' ? '¥' : currency === 'CAD' ? 'C$' : currency === 'AUD' ? 'A$' : `${currency} `;

  pdf.setFontSize(12);
  pdf.setTextColor(52, 73, 94);
  pdf.text('Sale Items:', margin, yPos);
  yPos += lineHeight;

  const products = invoiceData.product_details || [];
  let totalAmount = 0;

  // Column positions
  const col = {
    num: margin + 5,
    item: margin + 25,
    qty: margin + 90,
    unit: margin + 115,
    total: margin + 150,
  };

  pdf.setFontSize(10);
  pdf.setTextColor(255, 255, 255);
  pdf.setFillColor(52, 73, 94);
  pdf.rect(margin, yPos, pageWidth - margin * 2, lineHeight + 2, 'F');
  pdf.text('#', col.num, yPos + lineHeight * 0.7);
  pdf.text('Item', col.item, yPos + lineHeight * 0.7);
  pdf.text('QTY', col.qty, yPos + lineHeight * 0.7);
  pdf.text('Unit Price', col.unit, yPos + lineHeight * 0.7);
  pdf.text('Total Price', col.total, yPos + lineHeight * 0.7);
  yPos += lineHeight + 2;
  pdf.setTextColor(0, 0, 0);

  products.forEach((prod, idx) => {
    if (yPos > pageHeight - 80) {
      pdf.addPage();
      yPos = margin;
      // redraw header
      pdf.setFontSize(10);
      pdf.setTextColor(255, 255, 255);
      pdf.setFillColor(52, 73, 94);
      pdf.rect(margin, yPos, pageWidth - margin * 2, lineHeight + 2, 'F');
      pdf.text('#', col.num, yPos + lineHeight * 0.7);
      pdf.text('Item', col.item, yPos + lineHeight * 0.7);
      pdf.text('QTY', col.qty, yPos + lineHeight * 0.7);
      pdf.text('Unit Price', col.unit, yPos + lineHeight * 0.7);
      pdf.text('Total Price', col.total, yPos + lineHeight * 0.7);
      yPos += lineHeight + 2;
      pdf.setTextColor(0, 0, 0);
    }

    if (idx % 2 === 1) {
      pdf.setFillColor(248, 249, 250);
      pdf.rect(margin, yPos, pageWidth - margin * 2, lineHeight + 2, 'F');
    }

    pdf.setFontSize(9);
    pdf.text(`${idx + 1}.`, col.num, yPos + lineHeight * 0.7);

    const itemName = prod.items || 'Item';
    const itemLines = pdf.splitTextToSize(itemName, col.qty - col.item - 5);
    const rowHeight = Math.max(lineHeight + 2, itemLines.length * lineHeight);
    itemLines.forEach((l, i) => pdf.text(l, col.item, yPos + lineHeight * 0.7 + i * lineHeight));

    const qty = prod.quantity || 1;
    pdf.text(qty.toString(), col.qty + 3, yPos + lineHeight * 0.7);

    const unitPrice = parseFloat(prod.unit_price) || 0;
    pdf.text(`${currencySymbol}${unitPrice.toFixed(2)}`, col.unit, yPos + lineHeight * 0.7);

    const itemTotal = parseFloat(prod.price) || unitPrice * qty;
    totalAmount += itemTotal;
    pdf.text(`${currencySymbol}${itemTotal.toFixed(2)}`, col.total, yPos + lineHeight * 0.7);

    yPos += rowHeight + 1;
  });

  /* ------------------------- Summary -------------------------- */
  yPos += lineHeight * 1.5;
  pdf.setDrawColor(220, 220, 220);
  pdf.line(margin, yPos, pageWidth - margin, yPos);
  yPos += lineHeight * 1.5;

  const subtotal = invoiceData.price ? parseFloat(invoiceData.price) : totalAmount;
  const discountPct = invoiceData.discount ? parseFloat(invoiceData.discount) : null;
  const discountAmt = invoiceData.discounted_amount ? parseFloat(invoiceData.discounted_amount) : null;
  const taxAmt = invoiceData.tax ? parseFloat(invoiceData.tax) : null;
  const grandTotal = invoiceData.grand_total || subtotal - (discountAmt || 0) + (taxAmt || 0);

  const sumLabelX = pageWidth - margin - 120;
  const sumValX = pageWidth - margin - 10;

  pdf.setFontSize(11);
  pdf.setTextColor(0, 0, 0);
  pdf.text('Sub Total:', sumLabelX, yPos);
  pdf.text(`${currencySymbol}${subtotal.toFixed(2)}`, sumValX, yPos, { align: 'right' });
  yPos += lineHeight * 1.2;

  if (discountAmt && discountAmt > 0) {
    pdf.setTextColor(220, 53, 69);
    const discLabel = discountPct ? `Discount ${discountPct}%:` : 'Discount:';
    pdf.text(discLabel, sumLabelX, yPos);
    pdf.text(`${currencySymbol}${discountAmt.toFixed(2)}`, sumValX, yPos, { align: 'right' });
    pdf.setTextColor(0, 0, 0);
    yPos += lineHeight * 1.2;
  }
  if (taxAmt && taxAmt > 0) {
    pdf.text('Tax:', sumLabelX, yPos);
    pdf.text(`${currencySymbol}${taxAmt.toFixed(2)}`, sumValX, yPos, { align: 'right' });
    yPos += lineHeight * 1.2;
  }

  // Divider
  yPos += lineHeight * 0.5;
  pdf.setDrawColor(52, 73, 94);
  pdf.setLineWidth(0.5);
  pdf.line(sumLabelX, yPos, sumValX, yPos);
  yPos += lineHeight * 1.2;

  pdf.setFontSize(14);
  pdf.setTextColor(52, 73, 94);
  pdf.text('Grand Total:', sumLabelX, yPos);
  pdf.text(`${currencySymbol}${grandTotal.toFixed(2)}`, sumValX, yPos, { align: 'right' });

  /* ------------------------ Pagination ------------------------- */
  const pageCount = pdf.internal.pages.length - 1;
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setTextColor(128, 128, 128);
    pdf.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
  }

  /* -------------------------- Save ---------------------------- */
  const dateStamp = new Date().toISOString().split('T')[0];
  const custNamePart = invoiceData.patient_name ? invoiceData.patient_name.replace(/\s+/g, '_') : 'Customer';
  const fileName = `Guest_Invoice_${invoiceData.invoice_id || 'Unknown'}_${custNamePart}_${dateStamp}.pdf`;
  pdf.save(fileName);
};
